/**
 * PageTree — the workspace's page tree, rendered in the main sidebar
 * (FRIDAY-69).
 *
 * Sub-pages fold into their parent like an accordion: a chevron next to a
 * page with children expands or collapses it. The expanded pages are kept
 * per workspace in localStorage, and the ancestors of the open page are
 * always expanded so the current page is visible.
 *
 * Each row:
 *   - Left-click → navigate to the page (TanStack Router Link).
 *   - Right-click → opens the Notion-style `PageContextMenu` with
 *     Rename / Add sub-page / Move to… / Duplicate / Copy link /
 *     Archive (or Restore) / Delete.
 *   - "⋯" actions button (always shown on touch, on hover/focus on
 *     desktop) → opens the same menu, so it's reachable by touch and
 *     keyboard as well as right-click.
 *   - Double-click (or "Rename" from the menu) → inline edit the
 *     title; Enter commits, Escape cancels.
 *
 * Phase 2: static rendering, no drag-and-drop. Archived pages are left
 * out; they are listed in Workspace settings → Pages. Phase 4 will add the
 * dnd-kit reordering and reparenting.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import {
  usePageTree,
  useUpdatePage,
  useCreatePage,
  useArchivePage,
  useRestorePage,
  useDeletePageHard,
} from '@/features/notebook/pages'
import { buildTree, findPath, type PageNode } from '@/features/notebook/tree'
import { CreatePageDialog } from './CreatePageDialog'
import { PageContextMenu } from './PageContextMenu'
import { MovePageDialog } from './MovePageDialog'
import { MoveToWorkspaceDialog } from '@/components/workspace/MoveToWorkspaceDialog'
import { useWorkspaces } from '@/features/workspaces/workspaces'
import { toast } from '@/features/feedback/toast'
import { cn } from '@/lib/utils'
import type { Page } from '@friday/shared/realtime'

export interface PageTreeProps {
  wsId: string
  /** The highlighted page. Defaults to the `pageId` route param. */
  activePageId?: string
  /** Show a link to the notebook index next to the "+" in the header. */
  notebookLink?: boolean
}

interface MenuState {
  x: number
  y: number
  page: Page
  /** Element to refocus when the menu closes without moving focus. */
  returnFocus: HTMLElement | null
}

// Row affordances stay hidden until hover on desktop, but remain visible (with
// a 44px hit area) on touch devices and whenever keyboard focus is in the row.
const ROW_ACTION_CLASS =
  'rounded p-0.5 text-surface-400 opacity-0 hover:bg-surface-200 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 aria-expanded:opacity-100 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] [@media(hover:none)]:opacity-100 dark:hover:bg-surface-700'

export function PageTree({ wsId, activePageId: activePageIdProp, notebookLink }: PageTreeProps) {
  const params = useParams({ strict: false }) as { pageId?: string }
  const activePageId = activePageIdProp ?? params.pageId
  const tree = usePageTree(wsId, { includeArchived: true })
  const [creatingUnder, setCreatingUnder] = useState<string | null | undefined>(undefined)
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [moving, setMoving] = useState<string | null>(null)
  const [movingOut, setMovingOut] = useState<Page | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const navigate = useNavigate()

  // Archived pages live in Workspace settings → Pages, not in the tree. The
  // server archives one page at a time, so an archived page takes its whole
  // subtree out of the tree with it; restoring it brings the subtree back.
  const nodes = useMemo(() => withoutArchived(buildTree(tree.data ?? [])), [tree.data])
  const { isExpanded, toggle } = useExpandedPages(wsId, tree.data ?? [], activePageId)

  const openMenu = (e: React.MouseEvent<HTMLElement>, page: Page, trigger?: HTMLElement) => {
    e.preventDefault()
    e.stopPropagation()
    const active = document.activeElement
    const returnFocus =
      trigger ?? (active instanceof HTMLElement && e.currentTarget.contains(active) ? active : null)
    // The actions button and keyboard-invoked context menus (Shift+F10 /
    // Menu key) have no meaningful pointer position; anchor to the element.
    if (trigger || (e.clientX === 0 && e.clientY === 0)) {
      const rect = (trigger ?? e.currentTarget).getBoundingClientRect()
      setMenu({ x: rect.left, y: rect.bottom, page, returnFocus })
    } else {
      setMenu({ x: e.clientX, y: e.clientY, page, returnFocus })
    }
  }

  const closeMenu = () => {
    const el = menu?.returnFocus
    setMenu(null)
    // Restore focus only if the chosen action didn't move it elsewhere
    // (e.g. into the inline rename input or a dialog).
    requestAnimationFrame(() => {
      const active = document.activeElement
      if (el?.isConnected && (!active || active === document.body)) el.focus()
    })
  }

  return (
    <div className="px-2 py-2">
      <div className="text-surface-500 flex items-center justify-between px-2 py-1 text-[10px] font-semibold uppercase tracking-wider">
        <span>Pages</span>
        <div className="flex items-center gap-1">
          {notebookLink && (
            <Link
              to="/w/$wsId/n"
              params={{ wsId }}
              className="hover:bg-surface-100 dark:hover:bg-surface-800 rounded p-0.5"
              title="Open notebook"
              aria-label="Open notebook"
            >
              📓
            </Link>
          )}
          <button
            type="button"
            onClick={() => setCreatingUnder(null)}
            className="hover:bg-surface-100 dark:hover:bg-surface-800 rounded p-0.5"
            title="New top-level page"
            aria-label="New top-level page"
          >
            +
          </button>
        </div>
      </div>
      {tree.isLoading && <div className="text-surface-500 px-2 py-2 text-xs">Loading…</div>}
      {nodes.length === 0 && !tree.isLoading && (
        <button
          type="button"
          onClick={() => setCreatingUnder(null)}
          className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 block w-full px-2 py-2 text-left text-xs"
        >
          Create your first page →
        </button>
      )}
      <Tree
        nodes={nodes}
        wsId={wsId}
        activePageId={activePageId}
        isExpanded={isExpanded}
        onToggle={toggle}
        onAddChild={(id) => setCreatingUnder(id)}
        onContextMenu={openMenu}
        menuPageId={menu?.page.id}
        renamingId={renamingId}
        onRenamingChange={setRenamingId}
      />
      {/* The tree lives in the sidebar, whose transform would trap fixed
          dialogs inside its 240px column, so the dialogs render on body. */}
      {creatingUnder !== undefined &&
        createPortal(
          <CreatePageDialog
            wsId={wsId}
            defaultParentId={creatingUnder}
            onCreated={(pageId) => {
              setCreatingUnder(undefined)
              navigate({ to: '/w/$wsId/n/$pageId', params: { wsId, pageId } })
            }}
            onClose={() => setCreatingUnder(undefined)}
          />,
          document.body,
        )}
      <PageContextMenuWrapper
        menu={menu}
        onClose={closeMenu}
        onMove={(id) => setMoving(id)}
        onMoveToWorkspace={(page) => setMovingOut(page)}
        onRename={(id) => setRenamingId(id)}
        wsId={wsId}
      />
      {movingOut &&
        createPortal(
          <MoveToWorkspaceDialog
            kind="page"
            id={movingOut.id}
            label={movingOut.title}
            sourceWorkspaceId={wsId}
            onClose={() => setMovingOut(null)}
            onMoved={(result) => {
              setMovingOut(null)
              toast(`Moved "${movingOut.title}"`, { kind: 'success' })
              navigate({
                to: '/w/$wsId/n/$pageId',
                params: { wsId: result.targetWorkspaceId, pageId: result.id },
              })
            }}
          />,
          document.body,
        )}
      {moving && <MovePageDialog wsId={wsId} pageId={moving} onClose={() => setMoving(null)} />}
    </div>
  )
}

function Tree({
  nodes,
  wsId,
  activePageId,
  isExpanded,
  onToggle,
  onAddChild,
  onContextMenu,
  menuPageId,
  renamingId,
  onRenamingChange,
  depth = 0,
}: {
  nodes: PageNode[]
  wsId: string
  activePageId?: string
  isExpanded: (pageId: string) => boolean
  onToggle: (pageId: string) => void
  onAddChild: (parentId: string | null) => void
  onContextMenu: (e: React.MouseEvent<HTMLElement>, page: Page, trigger?: HTMLElement) => void
  menuPageId?: string
  renamingId: string | null
  onRenamingChange: (pageId: string | null) => void
  depth?: number
}) {
  return (
    <ul className={cn(depth === 0 ? '' : 'border-surface-200 dark:border-surface-800 ml-3 border-l pl-2')}>
      {nodes.map((n) => {
        const expanded = n.children.length > 0 && isExpanded(n.page.id)
        return (
          <li key={n.page.id}>
            <Row
              node={n}
              wsId={wsId}
              active={n.page.id === activePageId}
              expanded={expanded}
              onToggle={() => onToggle(n.page.id)}
              onAdd={() => onAddChild(n.page.id)}
              onContextMenu={(e, trigger) => onContextMenu(e, n.page, trigger)}
              menuOpen={n.page.id === menuPageId}
              renaming={n.page.id === renamingId}
              onRenamingChange={(next) => onRenamingChange(next ? n.page.id : null)}
            />
            {expanded && (
              <Tree
                nodes={n.children}
                wsId={wsId}
                activePageId={activePageId}
                isExpanded={isExpanded}
                onToggle={onToggle}
                onAddChild={onAddChild}
                onContextMenu={onContextMenu}
                menuPageId={menuPageId}
                renamingId={renamingId}
                onRenamingChange={onRenamingChange}
                depth={depth + 1}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}

function Row({
  node,
  wsId,
  active,
  expanded,
  onToggle,
  onAdd,
  onContextMenu,
  menuOpen,
  renaming,
  onRenamingChange: setRenaming,
}: {
  node: PageNode
  wsId: string
  active: boolean
  expanded: boolean
  onToggle: () => void
  onAdd: () => void
  onContextMenu: (e: React.MouseEvent<HTMLElement>, trigger?: HTMLElement) => void
  menuOpen: boolean
  renaming: boolean
  onRenamingChange: (renaming: boolean) => void
}) {
  const [draft, setDraft] = useState(node.page.title)
  const update = useUpdatePage(wsId)

  useEffect(() => {
    if (renaming) setDraft(node.page.title)
  }, [renaming, node.page.title])

  const commit = () => {
    const t = draft.trim()
    setRenaming(false)
    if (!t || t === node.page.title) {
      setDraft(node.page.title)
      return
    }
    update.mutate({
      pageId: node.page.id,
      version: node.page.version,
      patch: { title: t },
    })
  }

  return (
    <div
      onContextMenu={(e) => onContextMenu(e)}
      className={cn(
        'group flex items-center gap-1 rounded-md',
        active
          ? 'bg-brand-500/10 text-brand-600 dark:text-brand-300'
          : 'text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800',
      )}
    >
      {node.children.length > 0 ? (
        <button
          type="button"
          onClick={onToggle}
          className="text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-700 ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.page.title}`}
          title={expanded ? 'Collapse sub-pages' : 'Expand sub-pages'}
        >
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={cn('transition-transform duration-150', expanded && 'rotate-90')}
          />
        </button>
      ) : (
        <span className="ml-0.5 w-5 shrink-0" aria-hidden="true" />
      )}
      <Link
        to="/w/$wsId/n/$pageId"
        params={{ wsId, pageId: node.page.id }}
        aria-current={active ? 'page' : undefined}
        className="flex min-w-0 flex-1 items-center gap-2 truncate rounded-md py-1 pl-1 pr-2 text-sm"
        onDoubleClick={(e) => {
          e.preventDefault()
          setDraft(node.page.title)
          setRenaming(true)
        }}
      >
        <span className="text-sm">{node.page.icon ?? '📄'}</span>
        {renaming ? (
          <input
            autoFocus
            aria-label="Page title"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commit()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setDraft(node.page.title)
                setRenaming(false)
              }
            }}
            className="border-brand-400 dark:bg-surface-900 min-w-0 flex-1 rounded border bg-white px-1 py-0.5 text-sm outline-none"
          />
        ) : (
          <span className="truncate">{node.page.title}</span>
        )}
      </Link>
      <button
        type="button"
        onClick={(e) => onContextMenu(e, e.currentTarget)}
        className={ROW_ACTION_CLASS}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Page actions for ${node.page.title}`}
        title="Page actions"
      >
        ⋯
      </button>
      <button
        type="button"
        onClick={onAdd}
        className={ROW_ACTION_CLASS}
        aria-label={`New sub-page in ${node.page.title}`}
        title="New sub-page"
      >
        +
      </button>
    </div>
  )
}

/** Drops archived pages, and with them their sub-pages, from the tree. */
function withoutArchived(nodes: PageNode[]): PageNode[] {
  return nodes.filter((n) => !n.page.archivedAt).map((n) => ({ ...n, children: withoutArchived(n.children) }))
}

const EXPANDED_KEY_PREFIX = 'friday:page-tree-expanded:'

function readExpanded(wsId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_KEY_PREFIX + wsId)
    const ids = raw ? (JSON.parse(raw) as unknown) : []
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

/**
 * Accordion state of the tree. Pages start collapsed; the user's choices are
 * remembered per workspace. Opening a page expands its ancestors once, so the
 * current page is visible, and the user can still fold them afterwards.
 */
function useExpandedPages(wsId: string, pages: Page[], activePageId: string | undefined) {
  const [expanded, setExpanded] = useState<{ wsId: string; ids: Set<string> }>(() => ({
    wsId,
    ids: readExpanded(wsId),
  }))
  const ids = expanded.wsId === wsId ? expanded.ids : readExpanded(wsId)
  if (expanded.wsId !== wsId) setExpanded({ wsId, ids })

  // The ancestor chain as a string key, so the effect runs when the open
  // page (or its place in the tree) changes, not on every tree refetch.
  const ancestorKey = useMemo(
    () =>
      activePageId
        ? findPath(pages, activePageId)
            .slice(0, -1)
            .map((p) => p.id)
            .join('/')
        : '',
    [pages, activePageId],
  )
  useEffect(() => {
    if (!ancestorKey) return
    setExpanded((prev) => {
      const base = prev.wsId === wsId ? prev.ids : readExpanded(wsId)
      const missing = ancestorKey.split('/').filter((id) => !base.has(id))
      if (missing.length === 0 && prev.wsId === wsId) return prev
      return { wsId, ids: new Set([...base, ...missing]) }
    })
  }, [wsId, ancestorKey])

  useEffect(() => {
    try {
      window.localStorage.setItem(EXPANDED_KEY_PREFIX + wsId, JSON.stringify([...ids]))
    } catch {
      /* private mode / quota — the in-memory state still works */
    }
  }, [wsId, ids])

  const isExpanded = useCallback((pageId: string) => ids.has(pageId), [ids])
  const toggle = useCallback(
    (pageId: string) => {
      setExpanded((prev) => {
        const next = new Set(prev.wsId === wsId ? prev.ids : readExpanded(wsId))
        if (next.has(pageId)) next.delete(pageId)
        else next.add(pageId)
        return { wsId, ids: next }
      })
    },
    [wsId],
  )
  return { isExpanded, toggle }
}

/** Thin wrapper that resolves the context-menu actions for a page.
 *  Kept separate from `PageTree` so the action hooks (create, archive,
 *  delete, …) don't re-render every tree row on each menu open. */
function PageContextMenuWrapper({
  menu,
  onClose,
  onMove,
  onMoveToWorkspace,
  onRename,
  wsId,
}: {
  menu: MenuState | null
  onClose: () => void
  onMove: (pageId: string) => void
  onMoveToWorkspace: (page: Page) => void
  onRename: (pageId: string) => void
  wsId: string
}) {
  const create = useCreatePage(wsId)
  const archive = useArchivePage(wsId)
  const restore = useRestorePage(wsId)
  const del = useDeletePageHard(wsId)
  const update = useUpdatePage(wsId)
  const navigate = useNavigate()
  const { data: workspaces } = useWorkspaces()
  const role = workspaces?.find((w) => w.id === wsId)?.role
  const canMoveOut = role === 'owner' || role === 'admin'

  if (!menu) return null
  const page = menu.page
  const isArchived = !!page.archivedAt

  return (
    <PageContextMenu
      anchor={{ x: menu.x, y: menu.y }}
      pageTitle={page.title}
      isArchived={isArchived}
      onClose={onClose}
      onRename={() => onRename(page.id)}
      onAddSubPage={() => create.mutate({ parentPageId: page.id, title: 'Untitled' })}
      onMove={() => onMove(page.id)}
      onMoveToWorkspace={canMoveOut ? () => onMoveToWorkspace(page) : undefined}
      onCopyLink={() => {
        const url = `${window.location.origin}/w/${wsId}/n/${page.id}`
        void navigator.clipboard?.writeText(url).catch(() => undefined)
      }}
      onDuplicate={() =>
        create.mutate(
          {
            parentPageId: page.parentPageId,
            title: `${page.title} (copy)`,
            icon: page.icon,
            bodyMd: page.bodyMd,
          },
          { onSuccess: (p) => navigate({ to: '/w/$wsId/n/$pageId', params: { wsId, pageId: p.id } }) },
        )
      }
      onArchive={() =>
        archive.mutate(page.id, {
          onSuccess: () =>
            toast(`Archived "${page.title}"`, {
              action: { label: 'Undo', onSelect: () => restore.mutate(page.id) },
            }),
          onError: () => toast(`Could not archive "${page.title}"`, { kind: 'error' }),
        })
      }
      onRestore={() => restore.mutate(page.id)}
      onDelete={() => {
        if (window.confirm(`Delete "${page.title}"? This is permanent.`)) {
          del.mutate(page.id, { onSuccess: () => navigate({ to: '/w/$wsId', params: { wsId } }) })
        }
      }}
    />
  )
}
